import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { toast } from "sonner";
import type { Result } from "#/api";
import type { UserToken } from "#/entity";
import { GLOBAL_CONFIG } from "@/global-config";
import { t } from "@/locales/i18n";
import userStore from "@/store/userStore";
import { readPersistedUserToken } from "@/store/sessionStorage";
import { urlJoin } from "@/utils";
import {
	getAuthFailureMessage,
	isApiSuccessStatus,
	isAuthFailureStatus,
	shouldNotifyApiError,
} from "./apiClient.adapter";
import { type LoginResponse, toRefreshPayload } from "./services/userService.adapter";

export type ApiError = Error & { notified?: boolean };
export type ApiRequestConfig = AxiosRequestConfig & {
	_retry?: boolean;
	silent?: boolean;
	skipAuthRefresh?: boolean;
	rawResponse?: boolean;
};

export const shouldRefreshAuthFailure = (
	config: ApiRequestConfig | undefined,
	hasRefreshToken: boolean,
): config is ApiRequestConfig => Boolean(hasRefreshToken && config && !config._retry && !config.skipAuthRefresh);

export const shouldAttachAuthorization = (config: ApiRequestConfig, accessToken: string | undefined): boolean =>
	Boolean(accessToken && !config.skipAuthRefresh);

export const resolveRefreshFailure = (submitted: UserToken, current: UserToken): UserToken | null => {
	if (!submitted.refreshToken || !current.refreshToken || submitted.refreshToken === current.refreshToken) {
		return null;
	}
	return current;
};

const createApiError = (message: string, notified = false): ApiError => {
	const error = new Error(message) as ApiError;
	error.notified = notified;
	return error;
};

const axiosInstance = axios.create({
	baseURL: GLOBAL_CONFIG.apiBaseUrl,
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

let refreshTokenRequest: Promise<UserToken> | null = null;
let forbiddenRedirectTimer: ReturnType<typeof setTimeout> | null = null;

const redirectToLoginAfterForbidden = (message: string) => {
	if (forbiddenRedirectTimer) return;

	userStore.getState().actions.clearUserInfoAndToken();
	toast.error(message, { position: "top-center" });

	forbiddenRedirectTimer = setTimeout(() => {
		forbiddenRedirectTimer = null;
		const loginPath = urlJoin(GLOBAL_CONFIG.publicPath, "login");
		if (window.location.pathname !== loginPath) {
			window.location.replace(loginPath);
		}
	}, 800);
};

const refreshUserToken = async (): Promise<UserToken> => {
	const { userToken, actions } = userStore.getState();
	const refreshToken = userToken.refreshToken;
	if (!refreshToken) {
		throw createApiError("登录已过期，请重新登录");
	}
	const submittedToken = { ...userToken, refreshToken };

	if (!refreshTokenRequest) {
		const refreshConfig: ApiRequestConfig = {
			url: "/v1/refresh-token",
			method: "POST",
			data: toRefreshPayload(refreshToken),
			skipAuthRefresh: true,
		};

		refreshTokenRequest = axiosInstance
			.request<any, LoginResponse>(refreshConfig)
			.then((data) => {
				const nextToken = {
					accessToken: data.access_token,
					refreshToken: data.refresh_token,
				};
				actions.setUserToken(nextToken);
				return nextToken;
			})
			.catch((error) => {
				const nextToken = resolveRefreshFailure(submittedToken, readPersistedUserToken());
				if (nextToken) {
					actions.setUserToken(nextToken);
					return nextToken;
				}
				throw error;
			})
			.finally(() => {
				refreshTokenRequest = null;
			});
	}

	return refreshTokenRequest;
};

const retryRequestAfterRefresh = (originalRequest: ApiRequestConfig) => {
	originalRequest._retry = true;
	return refreshUserToken()
		.then((token) => {
			originalRequest.headers = {
				...originalRequest.headers,
				Authorization: `Bearer ${token.accessToken}`,
			};
			return axiosInstance.request<any, unknown>(originalRequest);
		})
		.catch((refreshError) => {
			userStore.getState().actions.clearUserInfoAndToken();
			const refreshMessage = getAuthFailureMessage(refreshError);
			if (shouldNotifyApiError(originalRequest.silent)) {
				toast.error(refreshMessage, { position: "top-center" });
			}
			return Promise.reject(refreshError);
		});
};

axiosInstance.interceptors.request.use(
	(config) => {
		const { accessToken } = userStore.getState().userToken;
		if (shouldAttachAuthorization(config as ApiRequestConfig, accessToken)) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
	(res: AxiosResponse<Result<any>>) => {
		if (!res.data) throw createApiError(t("sys.api.apiRequestFailed"));
		const originalRequest = res.config as ApiRequestConfig | undefined;
		if (originalRequest?.rawResponse) return res.data;
		const { status, data, message } = res.data;
		if (isApiSuccessStatus(status)) {
			return data;
		}
		const errMsg = message || t("sys.api.apiRequestFailed");
		if (
			isAuthFailureStatus(status) &&
			shouldRefreshAuthFailure(originalRequest, Boolean(userStore.getState().userToken.refreshToken))
		) {
			return retryRequestAfterRefresh(originalRequest);
		}
		if (Number(status) === 403) {
			if (shouldNotifyApiError(originalRequest?.silent)) {
				redirectToLoginAfterForbidden(errMsg);
			}
			throw createApiError(errMsg, shouldNotifyApiError(originalRequest?.silent));
		}
		if (originalRequest?.skipAuthRefresh) {
			throw createApiError(errMsg);
		}
		if (shouldNotifyApiError(originalRequest?.silent)) {
			toast.error(errMsg, { position: "top-center" });
		}
		throw createApiError(errMsg, shouldNotifyApiError(originalRequest?.silent));
	},
	(error: AxiosError<Result>) => {
		const { response, message } = error || {};
		const originalRequest = error.config as ApiRequestConfig | undefined;
		if (
			response?.status === 401 &&
			shouldRefreshAuthFailure(originalRequest, Boolean(userStore.getState().userToken.refreshToken))
		) {
			return retryRequestAfterRefresh(originalRequest);
		}
		const errMsg = response?.data?.message || message || t("sys.api.errorMessage");
		if (response?.status === 403) {
			if (shouldNotifyApiError(originalRequest?.silent)) {
				redirectToLoginAfterForbidden(errMsg);
			}
			return Promise.reject(createApiError(errMsg, shouldNotifyApiError(originalRequest?.silent)));
		}
		if (originalRequest?.skipAuthRefresh) {
			return Promise.reject(error);
		}
		if (shouldNotifyApiError(originalRequest?.silent)) {
			toast.error(errMsg, { position: "top-center" });
		}
		if (response?.status === 401 && shouldNotifyApiError(originalRequest?.silent)) {
			userStore.getState().actions.clearUserInfoAndToken();
		}
		return Promise.reject(error);
	},
);

class APIClient {
	get<T = unknown>(config: ApiRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "GET" });
	}
	post<T = unknown>(config: ApiRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "POST" });
	}
	put<T = unknown>(config: ApiRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PUT" });
	}
	delete<T = unknown>(config: ApiRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "DELETE" });
	}
	request<T = unknown>(config: ApiRequestConfig): Promise<T> {
		// The response interceptor unwraps the API envelope and resolves with `data`.
		return axiosInstance.request<any, T>(config) as Promise<T>;
	}
}

export default new APIClient();
